import { useState, useEffect, useContext } from "react";
import { post, C, LOGO_BASE } from "./utils";
import { useFileViewer, FilePill, ImageThumb, attachUrl, thumbUrl, splitChatbot, fileName } from "./FileViewer";
import { ThemeCtx, Icon } from "./AppShell";

const isImage = (p="") => /\.(jpg|jpeg|png|gif|webp|bmp)$/i.test(p);

const stripHtml= (h="") => h ? h.replace(/<[^>]*>/g," ").replace(/\s+/g," ").trim() : "";
const getLogoUrl = c => c?.COMPANY_LOGO && c.COMPANY_LOGO!=="all_companys.png"
  ? LOGO_BASE+c.COMPANY_LOGO : null;


// ─── Loaders ──────────────────────────────────────────────────────────────────
function useListLoader({ user, subproject, stateid }) {
  const [items,   setItems]   = useState([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!subproject || !user) return;
    setLoading(true); setItems([]);
    post("workshop_list", {
      subprojectid: subproject.SUBPROJECTID,
      userid:       user.USERID,
      stateid,
    }, user.AUTH_CODE)
      .then(r => { if(r.STATUS==="1") setItems(Array.isArray(r.DATA)?r.DATA:[]); })
      .catch(()=>{}).finally(()=>setLoading(false));
  }, [subproject?.SUBPROJECTID, stateid]);
  return { items, loading };
}

function useDetailLoader({ user, subproject, documentId, stateid }) {
  const [detail,  setDetail]  = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!subproject || !user || !documentId) return;
    setLoading(true); setDetail(null);
    post("workshop_list", {
      subprojectid: subproject.SUBPROJECTID,
      userid:       user.USERID,
      stateid,
      documentid:   documentId,
    }, user.AUTH_CODE)
      .then(r => {
        if(r.STATUS==="1"){
          const doc = Array.isArray(r.DATA) ? r.DATA[0] : (r.DATA||{});
          setDetail(doc);
        }
      })
      .catch(()=>{}).finally(()=>setLoading(false));
  }, [documentId, subproject?.SUBPROJECTID]);
  return { detail, loading };
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
function Spin({ color }) {
  return (
    <div style={{ display:"flex",alignItems:"center",justifyContent:"center",
      padding:"3rem",gap:"8px",color:"#9CA3AF",fontSize:"13px" }}>
      <div style={{ width:"14px",height:"14px",border:`2px solid #E5E7EB`,
        borderTopColor:color,borderRadius:"50%",animation:"_ws 0.8s linear infinite" }}/>
      <style>{"@keyframes _ws{to{transform:rotate(360deg)}}"}</style>
    </div>
  );
}


// ─── Left list item ───────────────────────────────────────────────────────────
function ListItem({ doc, active, onClick, tPrimary }) {
  const [hov, setHov] = useState(false);
  const companies = Array.isArray(doc.COMPANY)?doc.COMPANY:[];
  const co = companies.find(c=>c.PRIMARY===1)||companies[0]||null;
  const logoUrl = getLogoUrl(co);
  const date = doc.PUBLISHEDDATE||doc.DISPLAY_TIME||"";
  const details = Array.isArray(doc.WORKSHOP_DETAIL)?doc.WORKSHOP_DETAIL:[];

  return (
    <div onClick={onClick}
      onMouseEnter={()=>setHov(true)} onMouseLeave={()=>setHov(false)}
      style={{ padding:"10px 14px",cursor:"pointer",transition:"all 0.1s",
        borderLeft:`3px solid ${active?tPrimary:"transparent"}`,
        background:active?`${tPrimary}0E`:hov?"#F8FAFC":"transparent",
        borderBottom:"1px solid #F1F5F9" }}>
      <div style={{ display:"flex",gap:"8px",alignItems:"flex-start" }}>
        {/* Logo / initial */}
        <div style={{ width:"34px",height:"34px",borderRadius:"8px",flexShrink:0,
          border:`1px solid ${active?tPrimary+"44":"#E5E7EB"}`,
          background:active?`${tPrimary}12`:"#F8FAFC",
          display:"flex",alignItems:"center",justifyContent:"center",overflow:"hidden" }}>
          {logoUrl
            ? <img src={logoUrl} alt="" onError={e=>e.target.style.display="none"}
                style={{ width:"100%",height:"100%",objectFit:"contain" }}/>
            : <Icon name="workshop" size={15} color={active?tPrimary:"#9CA3AF"}/>}
        </div>
        <div style={{ flex:1,minWidth:0 }}>
          <div style={{ fontSize:"12px",fontWeight:active?"700":"600",
            color:active?tPrimary:"#111827",lineHeight:"1.3",
            overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap" }}>
            {doc.SUBJECT||"Untitled"}
          </div>
          <div style={{ display:"flex",alignItems:"center",gap:"5px",marginTop:"3px",flexWrap:"wrap" }}>
            {date && <span style={{ fontSize:"10px",color:"#9CA3AF" }}>{date}</span>}
            {details.length>0 && (
              <span style={{ fontSize:"9px",fontWeight:"700",
                color:active?tPrimary:"#6B7280",
                background:active?`${tPrimary}15`:"#F1F5F9",
                borderRadius:"8px",padding:"1px 6px" }}>
                {details.length} scenario{details.length!==1?"s":""}
              </span>
            )}

          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Workshop detail card ─────────────────────────────────────────────────────
function DetailCard({ d, idx, tPrimary, openFile, openImage }) {
  const [exp, setExp] = useState(true);
  const [hov, setHov] = useState(false);
  const [activeImg, setActiveImg] = useState(0);
  const companies = Array.isArray(d.COMPANY)?d.COMPANY:[];
  const co = companies.find(c=>c.PRIMARY===1)||companies[0]||null;
  const logoUrl = getLogoUrl(co);
  const chatbot = Array.isArray(d.CHATBOT)?d.CHATBOT:[];
  const images  = chatbot.filter(a=>isImage(a.FILEPATH));
  const files   = chatbot.filter(a=>!isImage(a.FILEPATH));
  const notes   = stripHtml(d.MESSAGES||"");

  // Accent color cycles per card
  const ACCENTS = ["#2563EB","#059669","#7C3AED","#D97706","#DB2777","#0891B2"];
  const accent  = ACCENTS[idx % ACCENTS.length];

  return (
    <div onMouseEnter={()=>setHov(true)} onMouseLeave={()=>setHov(false)}
      style={{ background:"#fff",borderRadius:"12px",overflow:"hidden",
        border:`1px solid ${hov?accent:"#E5E7EB"}`,transition:"all 0.15s",
        boxShadow:hov?`0 4px 20px ${accent}22`:"0 1px 4px rgba(0,0,0,0.04)" }}>

      {/* Colour accent bar */}
      <div style={{ height:"3px",background:accent }}/>

      {/* Header — clickable to expand/collapse */}
      <div onClick={()=>setExp(e=>!e)}
        style={{ padding:"12px 14px",cursor:"pointer",
          display:"flex",alignItems:"flex-start",gap:"10px" }}>

        {/* Scenario number */}
        <div style={{ width:"28px",height:"28px",borderRadius:"6px",flexShrink:0,
          background:`${accent}18`,border:`1px solid ${accent}33`,
          display:"flex",alignItems:"center",justifyContent:"center" }}>
          <span style={{ fontSize:"11px",fontWeight:"900",color:accent }}>{idx+1}</span>
        </div>

        <div style={{ flex:1,minWidth:0 }}>
          <div style={{ fontSize:"13px",fontWeight:"700",color:"#111827",
            lineHeight:"1.3",marginBottom:"4px" }}>
            {d.TITLE||d.BRANDNAME||"Untitled"}
          </div>
          <div style={{ display:"flex",alignItems:"center",gap:"6px",flexWrap:"wrap" }}>
            {/* Logo + sponsor */}
            {logoUrl && <img src={logoUrl} alt="" onError={e=>e.target.style.display="none"}
              style={{ width:"14px",height:"14px",objectFit:"contain",borderRadius:"2px" }}/>}
            {d.SPONSORNAME && <span style={{ fontSize:"10px",color:"#6B7280" }}>{d.SPONSORNAME}</span>}
            {d.BRANDNAME && d.BRANDNAME!=="Other" && (
              <span style={{ fontSize:"10px",fontWeight:"600",color:accent,
                background:`${accent}12`,borderRadius:"4px",padding:"1px 6px" }}>
                {d.BRANDNAME}
              </span>
            )}
            {d.PHASENAME && <span style={{ fontSize:"9px",color:"#9CA3AF",fontWeight:"500" }}>
              {d.PHASENAME}
            </span>}
            {d.INDICATIONNAME && <span style={{ fontSize:"9px",color:"#9CA3AF" }}>
              · {d.INDICATIONNAME}
            </span>}

          </div>
        </div>

        {/* Thumbnail + collapse toggle */}
        <div style={{ display:"flex",alignItems:"center",gap:"6px",flexShrink:0 }}>
          {images.length>0 && !exp && (
            <div onClick={e=>{e.stopPropagation();openImage(images[0].FILEPATH,images.map(i=>i.FILEPATH));}}
              style={{position:"relative",width:"40px",height:"40px",flexShrink:0,cursor:"pointer"}}>
              <ImageThumb filepath={images[0].FILEPATH}
                onClick={e=>{e.stopPropagation();openImage(images[0].FILEPATH,images.map(i=>i.FILEPATH));}}
                style={{width:"40px",height:"40px"}}/>
              {images.length>1&&<div style={{position:"absolute",top:"-5px",right:"-5px",
                width:"16px",height:"16px",borderRadius:"50%",
                background:accent,color:"#fff",fontSize:"8px",fontWeight:"800",
                display:"flex",alignItems:"center",justifyContent:"center",
                border:"2px solid #fff",zIndex:1}}>
                {images.length}
              </div>}
            </div>
          )}
          <span style={{ fontSize:"11px",color:"#9CA3AF",
            transform:exp?"rotate(180deg)":"none",transition:"transform 0.2s" }}>▼</span>
        </div>
      </div>

      {/* Expanded body */}
      {exp && (
        <div style={{ padding:"0 14px 14px",borderTop:"1px solid #F1F5F9" }}>

          {/* Thumbnail full */}
          {images.length>0 && (
            <div style={{marginTop:"10px",marginBottom:"10px"}}>
              {/* Main image */}
              <ImageThumb filepath={images[activeImg||0]?.FILEPATH||images[0].FILEPATH}
                onClick={()=>openImage(images[activeImg||0]?.FILEPATH||images[0].FILEPATH,images.map(i=>i.FILEPATH))}
                fit="contain" bg="#F1F5F9" useFull
                style={{width:"100%",height:"200px",borderRadius:"8px",marginBottom:"6px"}}/>
              {/* Filmstrip */}
              {images.length>1&&(
                <div style={{display:"flex",gap:"4px",overflowX:"auto",
                  scrollbarWidth:"none",msOverflowStyle:"none",
                  padding:"2px 0",WebkitOverflowScrolling:"touch"}}>
                  {images.map((img,j)=>(
                    <div key={j} onClick={()=>setActiveImg(j)}
                      style={{flexShrink:0,width:"44px",height:"44px",borderRadius:"5px",
                        overflow:"hidden",cursor:"pointer",transition:"all 0.15s",
                        border:`2px solid ${(activeImg||0)===j?accent:"transparent"}`,
                        opacity:(activeImg||0)===j?1:0.55}}>
                      <img src={thumbUrl(img.FILEPATH)} alt=""
                        onError={e=>e.target.style.display="none"}
                        style={{width:"100%",height:"100%",objectFit:"cover"}}/>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Messages */}
          {d.MESSAGES && (
            <div style={{ fontSize:"13px",color:"#374151",lineHeight:"1.75",
              marginTop:"10px",whiteSpace:"pre-line" }}>
              {d.MESSAGES}
            </div>
          )}

          {/* File attachments */}
          {files.length>0 && (
            <div style={{ display:"flex",flexWrap:"wrap",gap:"6px",marginTop:"10px" }}>
              {files.map((f,i)=><FilePill key={i} filepath={f.FILEPATH} onClick={()=>openFile(f.FILEPATH)}/>)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Right detail view ────────────────────────────────────────────────────────
function DetailView({ listDoc, detail, loading, tPrimary, openFile, openImage }) {
  if (!listDoc) return (
    <div style={{ flex:1,display:"flex",alignItems:"center",justifyContent:"center",
      flexDirection:"column",gap:"12px",background:"#F8FAFC" }}>
      <Icon name="workshop" size={52} color="#E5E7EB"/>
      <div style={{ fontSize:"14px",color:"#9CA3AF",fontWeight:"500" }}>
        Select a workshop to read
      </div>
    </div>
  );

  const doc      = detail || listDoc;
  const details  = Array.isArray(doc.WORKSHOP_DETAIL)?doc.WORKSHOP_DETAIL:[];
  const chatbot  = Array.isArray(doc.CHATBOT)?doc.CHATBOT:[];
  const images   = chatbot.filter(a=>isImage(a.FILEPATH));
  const files    = chatbot.filter(a=>!isImage(a.FILEPATH));
  const notes    = doc.NOTES||"";
  const companies= Array.isArray(listDoc.COMPANY)?listDoc.COMPANY:[];
  const co       = companies.find(c=>c.PRIMARY===1)||companies[0]||null;
  const logoUrl  = getLogoUrl(co);

  return (
    <div style={{ flex:1,display:"flex",flexDirection:"column",overflow:"hidden" }}>

      {/* ── Hero ── */}
      <div style={{ flexShrink:0,
        background:`linear-gradient(135deg,${tPrimary}F0 0%,${tPrimary}80 100%)`,
        padding:"20px 26px",position:"relative",overflow:"hidden" }}>
        {/* Decorative */}
        <div style={{ position:"absolute",right:"-20px",top:"-20px",
          width:"140px",height:"140px",borderRadius:"50%",
          background:"rgba(255,255,255,0.06)" }}/>
        <div style={{ position:"absolute",right:"60px",bottom:"-30px",
          width:"90px",height:"90px",borderRadius:"50%",
          background:"rgba(255,255,255,0.07)" }}/>

        <div style={{ position:"relative",display:"flex",gap:"14px",alignItems:"flex-start" }}>
          {/* Logo */}
          {logoUrl && (
            <div style={{ width:"44px",height:"44px",borderRadius:"10px",flexShrink:0,
              background:"rgba(255,255,255,0.2)",border:"1.5px solid rgba(255,255,255,0.3)",
              display:"flex",alignItems:"center",justifyContent:"center",overflow:"hidden" }}>
              <img src={logoUrl} alt="" onError={e=>e.target.style.display="none"}
                style={{ width:"80%",height:"80%",objectFit:"contain" }}/>
            </div>
          )}

          <div style={{ flex:1,minWidth:0 }}>
            {/* Label pill */}
            <div style={{ display:"inline-flex",alignItems:"center",gap:"5px",
              background:"rgba(255,255,255,0.2)",borderRadius:"20px",
              padding:"2px 10px",marginBottom:"6px" }}>
              <Icon name="workshop" size={10} color="rgba(255,255,255,0.9)"/>
              <span style={{ fontSize:"9px",fontWeight:"700",color:"rgba(255,255,255,0.9)",
                textTransform:"uppercase",letterSpacing:"0.8px" }}>Workshop</span>
            </div>

            <div style={{ fontSize:"18px",fontWeight:"900",color:"#fff",
              lineHeight:"1.25",marginBottom:"7px" }}>
              {listDoc.SUBJECT||"Untitled"}
            </div>

            <div style={{ display:"flex",alignItems:"center",gap:"10px",flexWrap:"wrap" }}>
              {(listDoc.PUBLISHEDDATE||listDoc.DISPLAY_TIME) && (
                <span style={{ fontSize:"10px",color:"rgba(255,255,255,0.8)",
                  display:"flex",alignItems:"center",gap:"3px" }}>
                  📅 {listDoc.PUBLISHEDDATE||listDoc.DISPLAY_TIME}
                </span>
              )}
              {details.length>0 && (
                <span style={{ fontSize:"10px",fontWeight:"700",color:"#fff",
                  background:"rgba(255,255,255,0.2)",borderRadius:"20px",padding:"2px 10px" }}>
                  {details.length} scenario{details.length!==1?"s":""}
                </span>
              )}
              {files.length>0 && (
                <span style={{ fontSize:"10px",fontWeight:"700",color:"#fff",
                  background:"rgba(255,255,255,0.15)",borderRadius:"20px",padding:"2px 10px" }}>
                  📎 {files.length} attachment{files.length!==1?"s":""}
                </span>
              )}
            </div>
          </div>

          {/* Thumbnail */}
          {images.length>0 && (
            <div onClick={()=>openImage(images[0].FILEPATH,images.map(i=>i.FILEPATH))}
              style={{position:"relative",flexShrink:0,cursor:"pointer"}}>
              <ImageThumb filepath={images[0].FILEPATH}
                onClick={()=>openImage(images[0].FILEPATH,images.map(i=>i.FILEPATH))}
                fit="contain" bg="rgba(255,255,255,0.15)"
                style={{width:"64px",height:"64px",flexShrink:0}}/>
              {images.length>1&&<div style={{position:"absolute",top:"-5px",right:"-5px",
                width:"18px",height:"18px",borderRadius:"50%",
                background:"rgba(255,255,255,0.9)",color:"#374151",
                fontSize:"9px",fontWeight:"800",
                display:"flex",alignItems:"center",justifyContent:"center",
                border:`2px solid rgba(255,255,255,0.5)`,zIndex:1}}>
                {images.length}
              </div>}
            </div>
          )}
        </div>
      </div>

      {/* ── Scrollable body ── */}
      <div style={{ flex:1,overflowY:"auto",background:"#F8FAFC",padding:"18px 22px" }}>
        {loading && <Spin color={tPrimary}/>}

        {!loading && <>
          {/* Top-level files */}
          {files.length>0 && (
            <div style={{ display:"flex",flexWrap:"wrap",gap:"6px",marginBottom:"14px" }}>
              {files.map((f,i)=><FilePill key={i} filepath={f.FILEPATH} onClick={()=>openFile(f.FILEPATH)}/>)}
            </div>
          )}

          {/* Notes */}
          {notes && (
            <div style={{ background:"#fff",borderRadius:"12px",
              border:"1px solid #E5E7EB",padding:"18px 20px",marginBottom:"16px",
              boxShadow:"0 1px 4px rgba(0,0,0,0.04)" }}>
              <div style={{ fontSize:"10px",fontWeight:"700",color:"#9CA3AF",
                textTransform:"uppercase",letterSpacing:"0.7px",marginBottom:"10px" }}>
                📋 Overview
              </div>
              <div style={{ fontSize:"13px",color:"#374151",lineHeight:"1.75",
                whiteSpace:"pre-line" }}>{notes}</div>
            </div>
          )}

          {/* Workshop details / scenarios */}
          {details.length>0 && (
            <div style={{ display:"flex",flexDirection:"column",gap:"10px" }}>
              {details.map((d,i)=>(
                <DetailCard key={d.DOCUMENTID||i} d={d} idx={i} tPrimary={tPrimary} openFile={openFile} openImage={openImage}/>
              ))}
            </div>
          )}
        </>}
      </div>
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────
export default function WorkshopScreen({ user, subproject }) {
  const [selected,   setSelected]   = useState(null);
  const [search,     setSearch]     = useState("");
  const [activeOnly, setActiveOnly] = useState(true);
  const ctx      = useContext(ThemeCtx)||{};
  const theme    = ctx.theme||ctx||{};
  const tPrimary = theme.primary||C.primary;
  const isEditor = subproject?.ROLE === 1;
  const stateid  = isEditor ? (activeOnly ? 2 : 1) : 2;

  const { items, loading:listLoading } = useListLoader({ user, subproject, stateid });
  const { lightbox, openFile, openImage } = useFileViewer();
  const { detail, loading:detailLoading } = useDetailLoader({
    user, subproject, documentId: selected?.DOCUMENTID, stateid
  });

  // Reset + auto-select on subproject change
  useEffect(()=>{ setSelected(null); }, [subproject?.SUBPROJECTID]);
  useEffect(()=>{ if(items.length>0) setSelected(items[0]); }, [items]);

  const filtered = search
    ? items.filter(d=>(d.SUBJECT||"").toLowerCase().includes(search.toLowerCase()))
    : items;

  return (
    <div style={{ display:"flex",height:"100%",overflow:"hidden",background:"#F0F4F8" }}>

      {/* ══ LEFT ════════════════════════════════════════════════════════════ */}
      <div style={{ width:"280px",flexShrink:0,display:"flex",flexDirection:"column",
        background:"#fff",borderRight:"1px solid #E4EAF2",overflow:"hidden" }}>

        {/* Header */}
        <div style={{ flexShrink:0,
          background:`linear-gradient(135deg,${tPrimary}EE,${tPrimary}88)`,
          padding:"14px 16px" }}>
          <div style={{ display:"flex",alignItems:"center",gap:"8px",marginBottom:"10px" }}>
            <Icon name="workshop" size={15} color="rgba(255,255,255,0.9)"/>
            <span style={{ fontSize:"14px",fontWeight:"800",color:"#fff",flex:1 }}>Workshop</span>
            <span style={{ fontSize:"11px",fontWeight:"700",
              color:"rgba(255,255,255,0.85)",background:"rgba(255,255,255,0.2)",
              borderRadius:"10px",padding:"1px 8px" }}>{items.length}</span>

            {/* Funnel — editor only */}
            {isEditor && (
              <button onClick={()=>{ setActiveOnly(a=>!a); setSelected(null); }}
                title={activeOnly?"Active — click for Inactive":"Inactive — click for Active"}
                style={{ width:"28px",height:"28px",borderRadius:"7px",border:"none",
                  cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",
                  background:activeOnly?"rgba(255,255,255,0.9)":"rgba(255,255,255,0.2)",
                  transition:"all 0.15s" }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                  stroke={activeOnly?tPrimary:"rgba(255,255,255,0.85)"}
                  strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
                </svg>
              </button>
            )}
          </div>

          {isEditor && (
            <div style={{ fontSize:"10px",fontWeight:"600",marginBottom:"8px",
              color:"rgba(255,255,255,0.75)",display:"flex",alignItems:"center",gap:"5px" }}>
              <div style={{ width:"6px",height:"6px",borderRadius:"50%",
                background:activeOnly?"#4ADE80":"#F87171" }}/>
              {activeOnly?"Active workshops":"Inactive workshops"}
            </div>
          )}

          {/* Search */}
          <div style={{ display:"flex",alignItems:"center",gap:"6px",
            background:"rgba(255,255,255,0.18)",borderRadius:"8px",
            padding:"5px 10px",border:"1px solid rgba(255,255,255,0.2)" }}>
            <Icon name="search" size={12} color="rgba(255,255,255,0.7)"/>
            <input value={search} onChange={e=>setSearch(e.target.value)}
              placeholder="Search workshops..."
              style={{ border:"none",background:"none",outline:"none",
                fontSize:"12px",color:"#fff",width:"100%",caretColor:"#fff" }}/>
            {search && (
              <button onClick={()=>setSearch("")}
                style={{ border:"none",background:"none",cursor:"pointer",
                  color:"rgba(255,255,255,0.7)",fontSize:"15px",padding:0,lineHeight:1 }}>×</button>
            )}
          </div>
        </div>

        {/* List */}
        <div style={{ flex:1,overflowY:"auto" }}>
          {listLoading && <Spin color={tPrimary}/>}
          {!listLoading && filtered.length===0 && (
            <div style={{ padding:"2rem",textAlign:"center",color:"#9CA3AF",fontSize:"13px" }}>
              No workshops found
            </div>
          )}
          {filtered.map(doc=>(
            <ListItem key={doc.DOCUMENTID} doc={doc}
              active={selected?.DOCUMENTID===doc.DOCUMENTID}
              onClick={()=>setSelected(doc)}
              tPrimary={tPrimary}/>
          ))}
        </div>
      </div>

      {/* ══ RIGHT ═══════════════════════════════════════════════════════════ */}
      <div style={{ flex:1,display:"flex",overflow:"hidden",minWidth:0 }}>
        <DetailView
          listDoc={selected}
          detail={detail}
          loading={detailLoading}
          tPrimary={tPrimary}
          openFile={openFile}
          openImage={openImage}/>
      </div>
    {lightbox}
    </div>
  );
}